/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { toJS } from 'mobx';
import { translate } from 'react-i18next';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Paper from '@material-ui/core/Paper';

//----------------------------------------------------------------------//
// GLOBAL COMPONENTS                                                    //
//----------------------------------------------------------------------//

import ActionDrawerWrapper from 'components/ActionDrawerWrapper/ActionDrawerWrapper';
import ActionsList from 'components/ActionsList/ActionsList';
import Activity from 'components/Activity/Activity';
import CompanyAssetsTable from 'components/Locations/CompanyAssetsTable';
import Loading from 'components/Loading/Loading';
import LocationDetails from 'components/Locations/LocationDetails/LocationDetails.js';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import PageTitle from 'components/PageTitleLegacy/PageTitle';
import UsersRoleExpandableList from 'components/User/UsersRoleExpandableList';
import StatusIndicator from 'components/StatusIndicator/StatusIndicator';
import WorkspacesExpandableList from 'components/Locations/WorkspacesExpandableList';

//----------------------------------------------------------------------//
// LOCAL COMPONENTS                                                     //
//----------------------------------------------------------------------//

import type { TypeActionDrawerStore } from 'stores/ActionDrawerStore';

import AssignUsers from 'views/ActionDrawer/forms/locations/AssignUsers/AssignUsers';
import RemoveUsers from 'views/ActionDrawer/forms/locations/RemoveUsers/RemoveUsers';
import ArchiveLocation from 'views/ActionDrawer/forms/locations/ArchiveLocation/ArchiveLocation';
import UnarchiveLocation from 'views/ActionDrawer/forms/locations/UnarchiveLocation/UnarchiveLocation';
import StartAuditByLocations from 'views/ActionDrawer/forms/StartAudit/StartAuditByLocations';

//----------------------------------------------------------------------//
// CSS                                                                  //
//----------------------------------------------------------------------//

import styles from 'common-scss/DetailsPage.module.scss';

import actionsListData from './actionsListData';
import { generateUrl, location } from 'api/routes';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

@translate()
@inject('navigationStore', 'accountStore', 'actionDrawerStore')
@observer
export default class LocationDetailsPage extends React.Component<*> {
  componentDidMount() {
    const accountStore: Object = this.props.accountStore;
    const accountId: string = this.props.match.params.id;
    const locationId: string = this.props.match.params.locationId;

    if (accountStore.accountId !== accountId) {
      accountStore.loadDetails(accountId);
    }

    if (!accountStore.locationDetails || accountStore.locationDetails.id !== locationId) {
      accountStore.loadLocationDetails(locationId);
    }

    this.props.navigationStore.pushPath(0, '/accounts');
    this.props.navigationStore.pushPath(1, `/accounts/${accountId}`, 0);
    this.props.navigationStore.pushPath(2, `/accounts/${accountId}`, 2);
  }

  componentWillUnmount() {
    const actionDrawerStore: TypeActionDrawerStore = this.props.actionDrawerStore;
    actionDrawerStore.clearActionDrawer();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.actionDrawerStore.loadParent) {
      const accountStore: Object = this.props.accountStore;
      const locationId: string = this.props.match.params.locationId;
      accountStore.loadLocationDetails(locationId);
      this.props.actionDrawerStore.loadParent = false;
    }
  }

  render(): React.Element<*> {
    const { locationDetails }: Object = this.props.accountStore;

    const companyId: string = this.props.match.params.companyId;
    const countryId: string = this.props.match.params.countryId;
    const loadingResources: boolean = this.props.accountStore.loadingResources;
    const details: Object = this.props.accountStore.details;
    const actionDrawerStore: TypeActionDrawerStore = this.props.actionDrawerStore;

    const t: Function = this.props.t;

    if (loadingResources || !details || !locationDetails) {
      return <Loading />;
    }

    let titleEnd: string = t('general:locations.labels.site');

    const titleEnds: Array<string> = ['building_transport', 'lab_room', 'system_workspace'];

    ['building', 'room', 'workspace'].forEach((item: string, index: number) => {
      if (Object.keys(locationDetails.hierarchy[item]).length > 0) {
        titleEnd = t(`general:locations.labels.${titleEnds[index]}`);
      }
    });

    const title: string = `
            ${t('general:trading_account_plural')} |
            ${details.details.name} |
            ${t('general:actions.manage_location_plural')} |
            ${locationDetails.name} (${titleEnd})`;

    return (
      <React.Fragment>
        <PageWrapper isFlex={false}>
          <PageTitle title={title} />

          <div className={styles['grid-wrapper']}>
            <Paper className={styles['feature-item']}>
              <div className={styles['details-panel']}>
                <LocationDetails
                  title={t('general:locations.labels.details')}
                  details={locationDetails}
                />

                <CompanyAssetsTable
                  title={t('general:locations.assigned_to_location')}
                  locationId={locationDetails.id}
                  assetsCount={locationDetails.all_assets_count}
                />

                {locationDetails.workspaces &&
                  Object.keys(toJS(locationDetails.workspaces)).length > 0 && (
                    <WorkspacesExpandableList
                      title={t('general:locations.location_system_workspaces')}
                      details={locationDetails}
                    />
                  )}

                <UsersRoleExpandableList
                  title={t('general:users.assigned_to_location')}
                  details={locationDetails}
                  companyId={companyId}
                  countryId={countryId}
                  locationId={locationDetails.id}
                  accountId={details.details.id}
                />
              </div>

              <div className={styles['actions-panel']}>
                <StatusIndicator
                  title={t('general:locations.labels.status')}
                  status={locationDetails.archived_at ? 'archived' : 'active'}
                />

                <ActionsList
                  actions={actionsListData}
                  data={{
                    details: details.details,
                    locationDetails,
                    titleEnd,
                    accountId: details.details.id,
                    locationId: locationDetails.id,
                  }}
                />
              </div>
            </Paper>

            <Paper className={styles['activity-panel']}>
              {this.props.match.params.locationId && (
                <Activity
                  title={true}
                  url={generateUrl(location.activity, [this.props.match.params.locationId])}
                  refresh={this.props.actionDrawerStore.success}
                  allowFilter={true}
                />
              )}
            </Paper>
          </div>
        </PageWrapper>

        <ActionDrawerWrapper
          onActionCompleted={() => {
            /*overriding with empty fn*/
          }}
        >
          {
            {
              assignUsers: (
                <AssignUsers accountId={details.details.id} location={locationDetails} />
              ),
              removeUsers: (
                <RemoveUsers accountId={details.details.id} location={locationDetails} />
              ),
              archiveLocation: <ArchiveLocation location={locationDetails} type="location" />,
              unarchiveLocation: <UnarchiveLocation location={locationDetails} type="location" />,
              startAudit: <StartAuditByLocations location={locationDetails} type="locations" />,
            }[actionDrawerStore.currentActionDrawer]
          }
        </ActionDrawerWrapper>
      </React.Fragment>
    );
  }
}
